'use client';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { KeyboardEvent, useCallback, useMemo, useState } from 'react';
import { Color } from '@diritto/common/style/color';
import { ColorPicker } from 'ui-components/common/ColorPicker';
import { IconButton } from 'ui-components/common/IconButton';
import { SelectBox } from 'ui-components/common/SelectBox';
import { Font, FontText } from 'constants/contentCover';
import { isPC } from 'constants/layout';
import { useMenuRef } from 'ui-hooks/useMenuRef';
import { DeleteIcon } from 'icons/DeleteIcon';
import { TextAddFiledIcon } from 'icons/TextAddFiledIcon';

interface EditCoverImageToolbarProps {
  className?: string;
  fontFamily: Font;
  fontFamilies: Font[];
  onClickAddText: () => void;
  onChangeFontStyle: (fontStyle: Font) => void;
  onChangeColor: (color: string) => void;
  onClickClear: () => void;
}

export const EditCoverImageToolbar = ({
  className,
  fontFamily,
  fontFamilies,
  onClickAddText,
  onChangeFontStyle,
  onChangeColor,
  onClickClear,
}: EditCoverImageToolbarProps) => {
  const { ref, isOpen: showColorPicker, setOpen: setShowColorPicker } = useMenuRef<HTMLDivElement>();
  const [textColor, setTextColor] = useState('');

  const selectList = useMemo(
    () =>
      fontFamilies.map(item => ({
        key: item,
        value: FontText[item],
        style: css`
          font-family: ${item};
        `,
      })),
    [fontFamilies],
  );

  const handleChangeColor = useCallback(
    (color: string) => {
      setTextColor(color);
      onChangeColor(color);
    },
    [onChangeColor],
  );

  // 색상 변경시 키보드 이벤트 전파 안되도록 하기 위함
  // 수동으로 변경시에 backspace 클릭시에 선택된 text 가 지워짐
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => e.stopPropagation(), []);

  return (
    <StyledToolbar className={className} onKeyDown={handleKeyDown}>
      <IconButton onClick={onClickAddText}>
        <TextAddFiledIcon />
      </IconButton>
      <StyledFontSelect>
        <SelectBox
          showCount={5}
          selectList={selectList}
          value={fontFamily}
          onChange={onChangeFontStyle}
          listBackgroundColor={Color.White}
          listPosition="top"
        />
      </StyledFontSelect>
      <div ref={ref}>
        <StyledTextColor color={textColor} onClick={() => setShowColorPicker(prev => !prev)} />
        {showColorPicker && <StyledColorPicker textColor={textColor} onChange={handleChangeColor} />}
      </div>
      <IconButton onClick={onClickClear}>
        <DeleteIcon />
      </IconButton>
    </StyledToolbar>
  );
};

const StyledToolbar = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  & svg {
    width: 30px;
    height: 30px;
  }
`;

const StyledFontSelect = styled.div`
  width: 100%;
  height: 100%;
  max-width: 180px;

  @media (${isPC}) {
    width: 180px;
    height: 48px;
  }
`;

const StyledTextColor = styled.div(
  ({ color }: { color: string }) => css`
    width: 45px;
    height: 45px;
    cursor: pointer;
    background-color: ${color};
    border-radius: 4px;
    border: 1px solid ${Color.Gray300};
  `,
);

const StyledColorPicker = styled(ColorPicker)`
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
`;
